import { useEffect, useState } from "react";
import { Button, Drawer, DrawerBody, DrawerFooter, DrawerHeader } from "@/components";
import { axiosInstance } from "@/helpers/axios";
import styles from "./ForceCatalogApproveDrawer.module.scss";

type ForceCatalogApproveDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  versionId: string | null;
  /** e.g. species name + version label */
  titleLine: string;
  onSuccess: () => void | Promise<void>;
};

export const ForceCatalogApproveDrawer = ({
  isOpen,
  onClose,
  versionId,
  titleLine,
  onSuccess,
}: ForceCatalogApproveDrawerProps) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setError(null);
      setSubmitting(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleConfirm = async () => {
    if (!versionId) return;
    setSubmitting(true);
    setError(null);
    try {
      await axiosInstance.post(
        `/v2/models/versions/${encodeURIComponent(versionId)}/force-approve`
      );
      await onSuccess();
      onClose();
    } catch (e: unknown) {
      const msg =
        e &&
        typeof e === "object" &&
        "response" in e &&
        (e as { response?: { data?: { message?: string } } }).response?.data?.message;
      setError(typeof msg === "string" ? msg : "Could not record force-approve.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer isOpen={isOpen} onClose={handleClose} ariaLabel="Force approve catalog version">
      <DrawerHeader
        title="Force approve"
        onClose={handleClose}
        closeDisabled={submitting}
      />
      <div className={styles["comp__stack"]}>
        <DrawerBody>
          <div className={styles["comp"]}>
            <div className={styles["comp__callout"]}>
              <p className={styles["comp__version"]}>{titleLine}</p>
              <p className={styles["comp__intro"]}>
                Normal approval requires a different account than the author. You can still approve your
                own submission as a <strong>force approve</strong>: it is stored in the database and
                skips independent review by others.
              </p>
            </div>
            <ul className={styles["comp__list"]}>
              <li>Your account will be recorded as the reviewer.</li>
              <li>This action is audited (force self-approve flag).</li>
              <li>Use only when you accept responsibility without a second reviewer.</li>
            </ul>
            {error ? <p className={styles["comp__error"]}>{error}</p> : null}
          </div>
        </DrawerBody>
        <DrawerFooter>
          <Button
            type="button"
            variant="secondary"
            size="medium"
            onClick={handleClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            size="medium"
            onClick={() => void handleConfirm()}
            disabled={submitting}
          >
            {submitting ? "Confirming…" : "Force approve"}
          </Button>
        </DrawerFooter>
      </div>
    </Drawer>
  );
};
